import { useLayoutEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { QueryRender } from "@/components/helpers/QueryRender"
import { useUser } from "@/entities/user/useUser"
import { useAttendanceEvents } from "@/entities/attendanceEvent/useAttendanceEvent"
import { useAbsenceRequests } from "@/entities/absenceRequest/useAbsenceRequest"
import { format } from "date-fns"
import { fr } from "date-fns/locale"
import { PageDialog } from "@/components/dialog/PageDialog"
import { AttendanceEventContent } from "@/pages/AttendanceEvent"
import { AddEventContent } from "./AddEventContent"
import { useCurrentSchoolYear } from "@/entities/schoolYear/useSchoolYear"
import { useAuth } from "@/hooks/useAuth"
import CalendarWidget from "@/components/attendance/CalendarWidget"
import { CopyableText } from "@/components/ui/CopyableText"
import { formatPhoneNumber } from "@/utils/formatPhoneNumber"
import { UserSmallProfile } from "@/components/ui/UserSmallProfile"
import { icon_edit } from "@/utils/svgIcons"

export default function ResumeDuJeunePage() {
   const { user } = useAuth()
   const { id } = useParams()
   const { data: currentSchoolYear } = useCurrentSchoolYear()
   const { data: young, ...query } = useUser({ id })
   const absenceRequestsQuery = useAbsenceRequests({ opt_youngId: id })
   const { data: attendanceEvents } = useAttendanceEvents({
      young_id: id,
      optionWhereNotJustified: true,
   })
   const [selectedEventId, setSelectedEventId] = useState<string | null>(null)
   const [isAddingEvent, setIsAddingEvent] = useState(false)

   useLayoutEffect(() => {
      window.scrollTo(0, 0)
   }, [])

   return (
      <>
         <QueryRender query={query} data={young}>
            {(young) => (
               <div className="">
                  <div className="">
                     {/* Personal Info Section */}
                     <div className="page-section">
                        <div className="">
                           <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                              <div className="w-full md:w-auto flex justify-between">
                                 <UserSmallProfile user={young} size="lg" />
                                 <div className=" md:hidden">
                                    <Link
                                       to={`/admin/utilisateur/${young.id}`}
                                       className="btn "
                                    >
                                       {icon_edit}
                                    </Link>
                                 </div>
                              </div>

                              <div className="flex flex-col gap-2">
                                 <CopyableText text={young.email} />

                                 {young.phone && (
                                    <CopyableText text={formatPhoneNumber(young.phone)} />
                                 )}
                              </div>

                              <div className="hidden md:block">
                                 <Link
                                    to={`/admin/utilisateur/${young.id}`}
                                    className="btn "
                                 >
                                    {icon_edit}
                                 </Link>
                              </div>
                           </div>
                        </div>
                     </div>

                     {/* Actions Section */}
                     <div className="page-section">
                        <div className="">
                           <h2 className="   ">Actions</h2>
                           <div className="flex gap-2">
                              <button
                                 className="btn btn-primary"
                                 onClick={() => setIsAddingEvent(true)}
                              >
                                 <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth={1.5}
                                    stroke="currentColor"
                                    className="w-6 h-6"
                                 >
                                    <path
                                       strokeLinecap="round"
                                       strokeLinejoin="round"
                                       d="M12 4.5v15m7.5-7.5h-15"
                                    />
                                 </svg>
                                 Ajouter un événement
                              </button>

                              <Link
                                 to={`/admin/statistiques/${young.id}`}
                                 className="btn btn-primary"
                              >
                                 Statistiques
                              </Link>
                           </div>
                        </div>
                     </div>

                     {/* Calendar Section */}
                     <div className="page-section">
                        <div className="">
                           <h2 className=" ">Calendrier</h2>
                           <CalendarWidget youngId={young.id} />
                        </div>
                     </div>

                     {/* TODO rm? Absence Requests Section   */}
                     <div className="page-section hidden">
                        <div className="">
                           <h2 className=" ">Demandes d'absence</h2>
                           <QueryRender query={absenceRequestsQuery} minHeight="0">
                              {(absenceRequests) => (
                                 <div className="grid gap-4">
                                    {absenceRequests.map((request) => (
                                       <Link
                                          key={request.id}
                                          to={`/admin/demande-absence/${request.id}`}
                                          className="card bg-base-200 shadow-sm hover:shadow-md transition-shadow"
                                       >
                                          <div className="card-body p-4">
                                             <div className="flex items-center justify-between">
                                                <div>
                                                   <p className="font-medium">
                                                      Du{" "}
                                                      {format(request.from, "d MMMM yyyy", {
                                                         locale: fr,
                                                      })}{" "}
                                                      au{" "}
                                                      {format(request.to, "d MMMM yyyy", {
                                                         locale: fr,
                                                      })}
                                                   </p>
                                                   <p className="text-sm text-gray-600">
                                                      Motif : {request.reason}
                                                   </p>
                                                </div>
                                                <div
                                                   className={`badge ${
                                                      request.status === "pending"
                                                         ? "badge-warning"
                                                         : request.status === "validated"
                                                         ? "badge-success"
                                                         : "badge-error"
                                                   }`}
                                                >
                                                   {request.status === "pending"
                                                      ? "En attente"
                                                      : request.status === "validated"
                                                      ? "Approuvée"
                                                      : "Refusée"}
                                                </div>
                                             </div>
                                          </div>
                                       </Link>
                                    ))}
                                 </div>
                              )}
                           </QueryRender>
                        </div>
                     </div>

                     {/* TODO rm? Unjustified Events Section */}
                     <div className="page-section hidden">
                        <div className="">
                           <h2 className=" ">Événements non justifiés</h2>
                           {attendanceEvents?.length === 0 ? (
                              <p className="text-gray-600">Aucun événement non justifié</p>
                           ) : (
                              <div className="grid gap-4">
                                 {attendanceEvents?.map((event) => (
                                    <div
                                       key={event.id}
                                       className="card bg-base-200 shadow-sm hover:cursor-pointer hover:shadow-md transition-shadow"
                                       onClick={() => setSelectedEventId(event.id)}
                                    >
                                       <div className="card-body p-4">
                                          <div className="flex items-center justify-between">
                                             <div>
                                                <p className="font-medium">
                                                   {format(event.from, "EEEE d MMMM yyyy", {
                                                      locale: fr,
                                                   })}
                                                   -{" "}
                                                   {format(event.to, "EEEE d MMMM yyyy", {
                                                      locale: fr,
                                                   })}
                                                </p>
                                                <p className="text-sm text-gray-600">
                                                   {event.type === "absent" ? "Absence" : "Retard"}
                                                </p>
                                             </div>
                                             <div className="badge badge-error">Non justifié</div>
                                          </div>
                                       </div>
                                    </div>
                                 ))}
                              </div>
                           )}
                        </div>
                     </div>
                  </div>
               </div>
            )}
         </QueryRender>

         <PageDialog isOpen={!!selectedEventId} onClose={() => setSelectedEventId(null)}>
            {selectedEventId && (
               <AttendanceEventContent id={selectedEventId} user={user!} variant="full" />
            )}
         </PageDialog>

         <PageDialog isOpen={isAddingEvent} onClose={() => setIsAddingEvent(false)}>
            {isAddingEvent && young && currentSchoolYear && (
               <AddEventContent
                  young={young}
                  schoolYearId={currentSchoolYear.id}
                  onClose={() => setIsAddingEvent(false)}
               />
            )}
         </PageDialog>
      </>
   )
}
